import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown } from 'lucide-react';
import { DEFAULT_EFFECT_TYPE, EFFECT_TYPES } from '../../config/effectTypes';
import { EFFECT_TYPE_LABELS_FI } from '../../config/effectTypes';

/**
 * EffectTypePicker — how does this consequence follow from its parent?
 *
 * specs/impact-path-v2-prd.md §3. Three types are always visible because they
 * cover almost every real answer and the fast path has to stay one click; the
 * remaining five sit behind "muu" so the tablet does not present eight
 * near-synonyms mid-workshop. The open question in the PRD is whether the long
 * tail survives real use — this shape lets us watch that instead of guessing.
 *
 * Polarity is shown as a small +/− so the systems meaning is visible without a
 * legend: blocks and weakens read as dampening, the rest as amplifying.
 */

const PRIMARY = ['leads_to', 'enables', 'blocks'];
const SECONDARY = ['reinforces', 'weakens', 'requires', 'delays', 'relates_to'];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${(props) => (props.$active ? '#fff' : 'rgba(255, 255, 255, 0.72)')};
  background: ${(props) =>
    props.$active ? 'rgba(76, 175, 80, 0.28)' : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid
    ${(props) => (props.$active ? 'rgba(76, 175, 80, 0.55)' : 'rgba(255, 255, 255, 0.14)')};

  &:hover {
    background: ${(props) =>
      props.$active ? 'rgba(76, 175, 80, 0.34)' : 'rgba(255, 255, 255, 0.11)'};
  }

  .polarity {
    font-size: 0.72rem;
    opacity: 0.65;
    font-weight: 600;
  }
`;

const MoreButton = styled(Chip)`
  background: transparent;
  border-style: dashed;

  .icon {
    width: 14px;
    height: 14px;
    transition: transform 0.15s ease;
    transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
  }
`;

const ChangeLink = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  color: rgba(33, 150, 243, 0.9);
  font-size: 0.82rem;
  text-decoration: underline;
  cursor: pointer;

  &:hover { color: #64b5f6; }
`;

const Label = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
`;

const polaritySign = (key) => {
  const p = EFFECT_TYPES[key]?.polarity;
  if (p === '+') return '+';
  if (p === '-') return '−';
  return '';
};

export default function EffectTypePicker({
  value = DEFAULT_EFFECT_TYPE,
  onChange,
  label = 'Miten tämä seuraa edellisestä?',
  disabled = false,
  // Collapsed: show the chosen type as one line with a "muuta" affordance.
  // Used in the create dialog, where the type has already been picked in the
  // step before it and the dialog should stay the dialog people know.
  collapsible = false,
}) {
  // Keep the long tail open when the current value lives there, so the
  // selection is never hidden behind a collapsed control.
  const [showAll, setShowAll] = useState(() => SECONDARY.includes(value));
  const [expanded, setExpanded] = useState(!collapsible);

  const renderChip = (key) => (
    <Chip
      key={key}
      type="button"
      $active={value === key}
      disabled={disabled}
      onClick={() => onChange?.(key)}
      title={key}
    >
      {EFFECT_TYPE_LABELS_FI[key]}
      <span className="polarity">{polaritySign(key)}</span>
    </Chip>
  );

  if (collapsible && !expanded) {
    return (
      <Wrap>
        <Row>
          <Label>Suhde:</Label>
          <Chip as="span" $active>
            {EFFECT_TYPE_LABELS_FI[value] || value}
            <span className="polarity">{polaritySign(value)}</span>
          </Chip>
          <ChangeLink type="button" onClick={() => setExpanded(true)} disabled={disabled}>
            muuta
          </ChangeLink>
        </Row>
      </Wrap>
    );
  }

  return (
    <Wrap>
      {label && <Label>{label}</Label>}
      <Row>
        {PRIMARY.map(renderChip)}
        {!showAll && (
          <MoreButton type="button" onClick={() => setShowAll(true)} disabled={disabled}>
            muu
            <ChevronDown className="icon" />
          </MoreButton>
        )}
      </Row>
      {showAll && <Row>{SECONDARY.map(renderChip)}</Row>}
    </Wrap>
  );
}
